import {
  ApolloClient,
  ApolloProvider,
  from,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { AssetReport } from "~/pages/reports/AssetReport/AssetReport";
import { ChecksOrVulnerabilitiesReport } from "~/pages/reports/ChecksOrVulnerabilitiesReport/ChecksOrVulnerabilitiesReport";
import { CveOrAdvisoryReport } from "~/pages/reports/CveOrAdvisoryReport/CveOrAdvisoryReport";
import { SecurityReport } from "~/pages/reports/SecurityReport/SecurityReport";
import { MondooThemeProvider } from "~/providers/mondoo-theme";
import { useRegions } from "~/providers/RegionsProvider";
import { lightTheme } from "~/themes";
import { createMondooTheme } from "~/themes/createTheme";
import { CheckReport } from "~/pages/reports/CheckReport/CheckReport";
import { typePolicies } from "~/providers/graphql";
import { onError } from "@apollo/client/link/error";
import * as Sentry from "@sentry/react";
import { Config } from "~/constants/configuration_provider";
import { truncateText } from "~/lib/truncateText";
import { ErrorBoundary, ErrorBoundaryProps } from "~/components/error-boundary";

export enum ReportType {
  Advisory = "ADVISORY",
  Checks = "CHECKS",
  Vulnerabilities = "VULNERABILITIES",
  Vulnerability = "VULNERABILITY",
}

const isSentryEnabled = Config.VITE_SENTRY_ENABLE === "true";

export function ReportingApp() {
  const { selectedGraphqlEndpoint } = useRegions();
  const [bearer, setBearer] = useState<null | string>(null);
  useEffect(() => {
    const fetchHeaders = async () => {
      try {
        // this is taken from https://stackoverflow.com/a/25490531
        setBearer(
          document.cookie.match("(^|;)\\s*apiToken\\s*=\\s*([^;]+)")?.pop() ||
            "",
        );
      } catch (error) {
        console.error("Error fetching headers:", error);
      }
    };

    fetchHeaders();
  }, []);

  const httpLink = new HttpLink({
    uri: selectedGraphqlEndpoint,
  });

  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        Authorization: bearer != null ? bearer : "",
      },
    };
  });

  const sentryErrorLink = onError(
    ({ operation, graphQLErrors, networkError }) => {
      if (!isSentryEnabled) return;

      const graphqlContext = {
        Graphql: {
          operationName: operation.operationName,
          variables: truncateText(JSON.stringify(operation.variables), 500),
        },
      };

      if (graphQLErrors) {
        graphQLErrors.forEach((graphqlError) => {
          const message = truncateText(graphqlError.message, 500);
          const path = truncateText(JSON.stringify(graphqlError.path), 500);
          const responseContext = {
            Response: {
              message,
              path,
            },
          };
          Sentry.captureException(
            new Error(`ReportingApp graphQLError: ${message}`),
            {
              contexts: {
                ...graphqlContext,
                ...responseContext,
              },
            },
          );
        });
      }

      if (networkError) {
        const message = truncateText(networkError.message, 500);
        const statusCode =
          "statusCode" in networkError ? networkError.statusCode : null;
        const result =
          "result" in networkError
            ? truncateText(JSON.stringify(networkError.result), 500)
            : null;
        const responseContext = {
          Response: {
            message,
            statusCode,
            result,
          },
        };
        Sentry.captureException(
          new Error(`ReportingApp networkError: ${message}`),
          {
            contexts: {
              ...graphqlContext,
              ...responseContext,
            },
          },
        );
      }
    },
  );

  const client = new ApolloClient({
    link: from([sentryErrorLink, authLink, httpLink]),
    cache: new InMemoryCache({
      typePolicies,
    }),
    devtools: {
      enabled: true,
    },
  });

  const theme = createMondooTheme(lightTheme);

  const boundaryOnCatch: ErrorBoundaryProps["onCatch"] = (error, info) => {
    if (isSentryEnabled) {
      const message = truncateText(error.message, 500);
      error.message = "ReportingApp boundaryError: " + message;
      Sentry.captureException(error, {
        contexts: { BoundaryError: { message, stack: info.componentStack } },
      });
    }
  };

  // wait to render the app for the first time until we have a bearer token
  if (bearer == null) {
    return "...initializing";
  }

  return (
    <ErrorBoundary key="reporting" onCatch={boundaryOnCatch}>
      <ApolloProvider client={client}>
        <BrowserRouter>
          <MondooThemeProvider defaultTheme={theme}>
            <Routes>
              <Route path="generated-report">
                <Route path="asset" element={<AssetReport />} />
                <Route path="check" element={<CheckReport />} />
                <Route path=":advisory" element={<CveOrAdvisoryReport />} />
                <Route
                  path=":vulnerability"
                  element={<CveOrAdvisoryReport />}
                />
                <Route path="security" element={<SecurityReport />} />
                <Route
                  path="checks"
                  element={
                    <ChecksOrVulnerabilitiesReport
                      reportType={ReportType.Checks}
                    />
                  }
                />
                <Route
                  path="vulnerabilities"
                  element={
                    <ChecksOrVulnerabilitiesReport
                      reportType={ReportType.Vulnerabilities}
                    />
                  }
                />
                <Route
                  path="*"
                  element={<div>There is no asset available</div>}
                />
              </Route>
              <Route path="*" element={<div>fallback</div>} />
            </Routes>
          </MondooThemeProvider>
        </BrowserRouter>
      </ApolloProvider>
    </ErrorBoundary>
  );
}
